/* HPN NAD3 Extra Strength - Try3 (scoped, no globals) */
(() => {
  const SELECTOR_ROOT = '[data-hpn-nad3-extra-strength-try3]';
  const SELECTOR_CTA = '[data-hpn-nad3-extra-strength-try3-cta]';

  function parseDiscountCodes(raw) {
    const text = (raw || '').trim();
    if (!text) return [];

    return text
      .split(/[\n,]+/g)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  async function postJSON(url, bodyObj) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(bodyObj),
      credentials: 'same-origin',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');

      throw new Error(msg);
    }

    return data;
  }

  async function addItemsBatch(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: items || [] }),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');

      throw new Error(msg);
    }

    return data;
  }

  async function addSingleItem(item) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(item || {}),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');

      throw new Error(msg);
    }

    return data;
  }

  function wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  function setStatus(root, msg) {
    const el = root.querySelector('[data-hpn-nad3-extra-strength-try3-status]');
    if (!el) return;

    el.textContent = msg || '';
  }

  function setBusy(btn, isBusy) {
    if (!btn) return;

    btn.disabled = !!isBusy;
    btn.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    if (isBusy) {
      btn.classList.add('is-busy');
    } else {
      btn.classList.remove('is-busy');
    }
  }

  function getScrollTargetElement(rawTarget) {
    const targetId = (rawTarget || '').trim().replace(/^#/, '');

    if (!targetId) return null;

    return document.getElementById(targetId);
  }

  function handleScrollClick(btn) {
    const root = btn.closest(SELECTOR_ROOT);
    if (!root) return;

    const rawTarget =
      btn.getAttribute('data-hpn-nad3-extra-strength-try3-scroll-target') || '';
    const targetId = rawTarget.trim().replace(/^#/, '');
    const targetEl = getScrollTargetElement(rawTarget);

    if (!targetEl) {
      setStatus(
        root,
        targetId
          ? `Could not find section ID "${targetId}". Please check the CTA scroll target setting.`
          : 'Missing scroll target section ID. Please set it in the section settings.'
      );
      return;
    }

    setStatus(root, '');

    targetEl.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  function handleCtaClick(btn) {
    const action = (
      btn.getAttribute('data-hpn-nad3-extra-strength-try3-cta-action') || 'checkout'
    )
      .trim()
      .toLowerCase();

    if (action === 'scroll') {
      handleScrollClick(btn);
      return;
    }

    handleCheckoutClick(btn);
  }

  async function handleCheckoutClick(btn) {
    const root = btn.closest(SELECTOR_ROOT);
    if (!root) return;

    if (root.__hpnNad3ExtraStrengthTry3CheckoutSubmitting) return;
    root.__hpnNad3ExtraStrengthTry3CheckoutSubmitting = true;

    setStatus(root, '');
    setBusy(btn, true);

    try {
      const clearCart =
        btn.getAttribute('data-hpn-nad3-extra-strength-try3-clear-cart') === '1';

      const mainVariantIdRaw = (
        btn.getAttribute('data-hpn-nad3-extra-strength-try3-main-variant') || ''
      ).trim();

      const mainQty = Number(
        btn.getAttribute('data-hpn-nad3-extra-strength-try3-main-qty') || 1
      );

      const mainSellingPlanRaw = (
        btn.getAttribute('data-hpn-nad3-extra-strength-try3-main-selling-plan') || ''
      ).trim();

      const mainVariantId = Number(mainVariantIdRaw || 0);
      const mainSellingPlanId = Number(mainSellingPlanRaw || 0);

      if (!mainVariantId) {
        throw new Error('Missing main variant ID. Please set it in the section settings.');
      }

      let freebies = [];
      const freebiesRaw =
        btn.getAttribute('data-hpn-nad3-extra-strength-try3-freebies') || '[]';

      try {
        freebies = JSON.parse(freebiesRaw) || [];
      } catch (e) {
        freebies = [];
      }

      const mainItem = {
        id: mainVariantId,
        quantity: Math.max(1, mainQty || 1),
      };

      if (mainSellingPlanRaw && mainSellingPlanId > 0) {
        mainItem.selling_plan = mainSellingPlanId;
      }

      const freebieItems = (freebies || [])
        .filter((it) => it && Number(it.id) > 0)
        .map((it) => ({
          id: Number(it.id),
          quantity: Math.max(1, Number(it.quantity) || 1),
        }));

      const discountCodes = parseDiscountCodes(
        btn.getAttribute('data-hpn-nad3-extra-strength-try3-discounts')
      );

      const checkoutUrl =
        discountCodes.length > 0
          ? `/checkout?discount=${encodeURIComponent(discountCodes.join(','))}`
          : '/checkout';

      if (clearCart) {
        await postJSON('/cart/clear.js', {});
        await wait(180);
      }

      await addSingleItem(mainItem);

      if (freebieItems.length) {
        try {
          await addItemsBatch(freebieItems);
        } catch (freebieErr) {
          console.warn('HPN NAD3 Extra Strength Try3 freebies failed to add:', freebieErr);
        }
      }

      window.location.href = checkoutUrl;
      return;
    } catch (err) {
      setStatus(
        root,
        err && err.message ? err.message : 'Unable to start checkout. Please try again.'
      );
      setBusy(btn, false);
      root.__hpnNad3ExtraStrengthTry3CheckoutSubmitting = false;
    }
  }

  function bindRoot(root) {
    if (!root || root.__hpnNad3ExtraStrengthTry3Bound) return;
    root.__hpnNad3ExtraStrengthTry3Bound = true;

    const cta = root.querySelector(SELECTOR_CTA);

    if (cta) {
      cta.addEventListener('click', () => handleCtaClick(cta));
    }
  }

  function init() {
    document.querySelectorAll(SELECTOR_ROOT).forEach(bindRoot);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();